import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import request from "util/request";
import { segmentTrack } from "util/segment";
import { hardNavigateToUnsafe } from "util/navigation";
import { useCompanyName } from "util/feature_detection";

import type { SsoProvider } from "./proof/screens/saml";
import { Continue, SamlLoginHeader, BackButton, BackToPasswordScreen } from "./components";
import Styles from "./index.module.scss";

type SsoLoginInput = {
  email: string;
  ssoProvider: SsoProvider;
  redirectUrl: string | null | undefined;
};

const MESSAGES = defineMessages({
  backButtonAriaLabel: {
    id: "095099d5-3f8e-4def-a77b-0da0c4dbf14e",
    defaultMessage: "Back to email input",
  },
});

function ssoLogin({ email, ssoProvider, redirectUrl }: SsoLoginInput): Promise<void> {
  segmentTrack("Attempted Saml Login");
  return request("post", "saml/login", {
    email,
    redirect_url: redirectUrl,
    identity_provider_id: ssoProvider.id,
  }).then((json) => hardNavigateToUnsafe(json.idp_url));
}

type Props = {
  onBack: () => void;
  backToPasswordScreen: () => void;
  enablePasswordAuth: boolean;
  email: string;
  ssoProvider: SsoProvider;
};

const translateErrorMessage = (key: string, companyName: string) => {
  switch (key) {
    case "InvalidEmail":
      return (
        <FormattedMessage
          id="2640c999-b53b-477a-9676-11e7e8475ed0"
          defaultMessage="Invalid email address."
        />
      );
    case "InvalidIdentityProvider":
      return (
        <FormattedMessage
          id="97dd5f7a-dc10-45e1-a9f3-c240d2817f93"
          defaultMessage="Invalid identity provider. Please contact {companyName} support."
          values={{ companyName }}
        />
      );
    case "InvalidLogoutRequest":
      return (
        <FormattedMessage
          id="65844a67-e44b-48c2-86b1-8fde7b04fff3"
          defaultMessage="Invalid logout request. Please contact your company's IT department."
        />
      );
    case "InvalidRedirectUrl":
      return (
        <FormattedMessage
          id="bc902f4a-7cfc-4640-a4e6-6ccd501af574"
          defaultMessage="Invalid redirect URL. Please contact your company's IT department."
        />
      );
    case "InvalidOrganization":
      return (
        <FormattedMessage
          id="5d81bc13-3f8b-4f86-b89f-8dfc9f54f5c4"
          defaultMessage="Invalid organization. Please contact your company's IT department."
        />
      );
    case "InvitationRequired":
      return (
        <FormattedMessage
          id="3b7a0ce6-1658-45be-b7f6-8e424cd807ce"
          defaultMessage="A {companyName} account with this email address already exists. Reach out to your admin to invite you as a team member."
          values={{ companyName }}
        />
      );
    case "ProviderAlreadyConnected":
      return (
        <FormattedMessage
          id="93f87b21-6e82-4175-b7ff-65114b2b558c"
          defaultMessage="A {companyName} account with this email address already exists in a different organization. Reach out to support if you need to switch organizations."
          values={{ companyName }}
        />
      );
    default:
      return (
        <FormattedMessage
          id="a8259b56-7e68-4a6f-9b7a-87444477fff5"
          defaultMessage="Please contact {companyName} support."
          values={{ companyName }}
        />
      );
  }
};

export function ErrorMessage({ errorMsg }: { errorMsg: string }) {
  const companyName = useCompanyName();
  return (
    <>
      <p className={Styles.error}>
        <FormattedMessage
          id="f1a01521-9c76-4bc9-b688-f7b3fcc47cf1"
          defaultMessage="Your Single Sign On authentication has failed."
        />
      </p>
      <p className={Styles.error}>{translateErrorMessage(errorMsg, companyName)}</p>
    </>
  );
}

export default function SamlScreen({
  onBack,
  backToPasswordScreen,
  enablePasswordAuth,
  email,
  ssoProvider,
}: Props) {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const [samlError, setSamlError] = useState<string | null>(null);
  const onClick = () => {
    ssoLogin({ email, ssoProvider, redirectUrl: redirect && decodeURIComponent(redirect) }).catch(
      (e) => setSamlError(e.body.error),
    );
  };

  return (
    <>
      <SamlLoginHeader ssoProvider={ssoProvider} />
      <Continue onClick={onClick} />
      {samlError && <ErrorMessage errorMsg={samlError} />}
      <BackButton
        className={Styles.samlBackBtn}
        onBack={onBack}
        aria-label={intl.formatMessage(MESSAGES.backButtonAriaLabel)}
      />
      {enablePasswordAuth && <BackToPasswordScreen backToPasswordScreen={backToPasswordScreen} />}
    </>
  );
}
